import type { Middleware } from '@reduxjs/toolkit';
import { loginSuccess, logout } from '@sunstone/store';
import type { AuthUser } from '@sunstone/types';
import type { AuthClaims } from '../../features/auth/useAuthClaims';

/**
 * Auth0 sync middleware
 *
 * This middleware listens for authentication state changes and ensures
 * the Redux store is kept in sync with Auth0 authentication state.
 *
 * Note: This middleware is designed to be triggered by a custom action
 * dispatched when Auth0 authentication state changes.
 */

// Custom action type for syncing Auth0 state
export const AUTH0_SYNC = 'auth0/sync' as const;
export const AUTH0_LOGOUT = 'auth0/logout' as const;

interface Auth0SyncAction {
  type: typeof AUTH0_SYNC;
  payload: AuthClaims;
  [key: string]: unknown;
}

interface Auth0LogoutAction {
  type: typeof AUTH0_LOGOUT;
  [key: string]: unknown;
}

/**
 * Action creator for syncing Auth0 claims to Redux
 */
export function syncAuth0Claims(claims: AuthClaims): Auth0SyncAction {
  return {
    type: AUTH0_SYNC,
    payload: claims,
  };
}

/**
 * Action creator for Auth0 logout
 */
export function syncAuth0Logout(): Auth0LogoutAction {
  return {
    type: AUTH0_LOGOUT,
  };
}

/**
 * Converts Auth0 claims to AuthUser format for Redux store
 */
function claimsToAuthUser(claims: AuthClaims): AuthUser {
  return {
    id: claims.userId,
    email: claims.email,
    name: claims.name,
    emailVerified: claims.emailVerified,
    picture: claims.picture,
    organizationId: claims.organizationId,
    organizationName: claims.organizationName,
    tenantId: claims.tenantId,
    permissions: claims.permissions,
    scope: claims.scope,
    userMetadata: claims.userMetadata,
  };
}

/**
 * Redux middleware for syncing Auth0 authentication state
 *
 * This middleware intercepts AUTH0_SYNC and AUTH0_LOGOUT actions and
 * dispatches the appropriate Redux auth actions to keep the store in sync.
 */
export const authSyncMiddleware: Middleware = (store) => (next) => (action) => {
  // Check if this is an Auth0 sync action
  if (isAuth0SyncAction(action)) {
    const authUser = claimsToAuthUser(action.payload);
    store.dispatch(loginSuccess(authUser));
    return next(action);
  }

  // Check if this is an Auth0 logout action
  if (isAuth0LogoutAction(action)) {
    store.dispatch(logout());
    return next(action);
  }

  // Pass through all other actions
  return next(action);
};

/**
 * Type guard for Auth0 sync action
 */
function isAuth0SyncAction(action: unknown): action is Auth0SyncAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    action.type === AUTH0_SYNC &&
    'payload' in action
  );
}

/**
 * Type guard for Auth0 logout action
 */
function isAuth0LogoutAction(action: unknown): action is Auth0LogoutAction {
  return (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    action.type === AUTH0_LOGOUT
  );
}
